import { INPUT_TYPES } from '@shared/tabbed-table/constants';

export const LOAD_FIELDS = {
	REFERENCE_NUMBER: 'Reference Number',
	PICKUP_DATE: 'Pickup Date',
	ORIGIN: 'Origin',
	DESTINATION: 'Destination',
	EQUIPMENT: 'Equipment',
	RATE: 'Load Rate',
	BIDDING_LINK: 'Bid',
	OFFERED_RATE: 'Offered Rate',
	DELIVERY_DATE: 'Delivery Date',
	USER: 'User',
	STATUS: 'Status',
	LANE: 'Lane',
	STOPS: 'Stops',
	ORIGIN_CITY: 'Origin City',
	ORIGIN_STATE: 'Origin State',
	DESTINATION_CITY: 'Destination City',
	DESTINATION_STATE: 'Destination State',
	CREATED: 'Created',
	COMPANY: 'Company',
	BOOKED_RATE: 'Booked Rate',
	ASSIGNED_ON: 'Assigned On',
	PO_NUMBER: 'PO Number',
	BROKER_ONBOARD_LINK: 'Onboarding Link',
	WEIGHT: 'Weight',
};

export function buildHeaders(isBookedLoads: boolean = false, rowSelectableEnabled: boolean) {
	if (isBookedLoads) {
		return [
			// add "select" only when (not booked) OR (booked + selectable)
			...(rowSelectableEnabled ? [{ dataName: 'select', formattedName: 'select' }] : []),
			{ dataName: 'reference_number', formattedName: LOAD_FIELDS.REFERENCE_NUMBER },
			{ dataName: 'po_number', formattedName: LOAD_FIELDS.PO_NUMBER },
			{
				dataName: 'pickupDate',
				formattedName: LOAD_FIELDS.PICKUP_DATE,
				dataFields: {
					date: 'pickupDate',
					openTime: 'pickup_open_time',
					appointmentRequired: 'pickup_appointment_required',
					closeTime: 'pickup_close_time',
				},
			},
			{
				dataName: 'deliveryDate',
				formattedName: LOAD_FIELDS.DELIVERY_DATE,
				dataFields: {
					date: 'deliveryDate',
					openTime: 'delivery_open_time',
					appointmentRequired: 'delivery_appointment_required',
					closeTime: 'delivery_close_time',
				},
			},
			{ dataName: 'lane', formattedName: LOAD_FIELDS.LANE },
			{ dataName: 'stopsCount', formattedName: LOAD_FIELDS.STOPS },
			{ dataName: 'rate', formattedName: LOAD_FIELDS.BOOKED_RATE },
			{ dataName: 'business_name', formattedName: LOAD_FIELDS.COMPANY },
			{ dataName: 'userInfo', formattedName: LOAD_FIELDS.USER },
			{ dataName: 'assignedAt', formattedName: LOAD_FIELDS.ASSIGNED_ON },
		];
	} else {
		return [
			{ dataName: 'select', formattedName: 'select' },
			{ dataName: 'reference_number', formattedName: LOAD_FIELDS.REFERENCE_NUMBER },
			{ dataName: 'po_number', formattedName: LOAD_FIELDS.PO_NUMBER },
			{
				dataName: 'pickupDate',
				formattedName: LOAD_FIELDS.PICKUP_DATE,
				dataFields: {
					date: 'pickupDate',
					openTime: 'pickup_open_time',
					appointmentRequired: 'pickup_appointment_required',
					closeTime: 'pickup_close_time',
				},
			},
			{ dataName: 'lane', formattedName: LOAD_FIELDS.LANE },
			{ dataName: 'stopsCount', formattedName: LOAD_FIELDS.STOPS },
			{ dataName: 'rate', formattedName: LOAD_FIELDS.RATE },
			{ dataName: 'offeredRate', formattedName: LOAD_FIELDS.OFFERED_RATE },
			{
				dataName: 'deliveryDate',
				formattedName: LOAD_FIELDS.DELIVERY_DATE,
				dataFields: {
					date: 'deliveryDate',
					openTime: 'delivery_open_time',
					appointmentRequired: 'delivery_appointment_required',
					closeTime: 'delivery_close_time',
				},
			},
			{ dataName: 'business_name', formattedName: LOAD_FIELDS.COMPANY },
			{ dataName: 'userInfo', formattedName: LOAD_FIELDS.USER },
			{ dataName: 'broker_onboard_link', formattedName: LOAD_FIELDS.BROKER_ONBOARD_LINK },
		];
	}
}

export function buildHeaderDataTypes(isBookedLoads: boolean = false) {
	if (isBookedLoads) {
		return [
			'string',
			'string',
			'quoteDateTime',
			'quoteDateTime',
			'string',
			'number',
			'currency',
			'string',
			'userInfo',
			'string',
		];
	} else {
		return [
			'string',
			'string',
			'quoteDateTime',
			'string',
			'number',
			'currency',
			'currency',
			'quoteDateTime',
			'string',
			'userInfo',
			'onboardingLink',
		];
	}
}

export function buildDisplayedColumns(
	isSmallScreen: boolean,
	isBookedLoads: boolean = false,
	rowSelectableEnabled: boolean
) {
	let columns = [];

	if (isSmallScreen) {
		if (isBookedLoads) {
			columns = [
				...(rowSelectableEnabled ? ['select'] : []),
				'pickupDate',
				...(rowSelectableEnabled ? [] : ['deliveryDate']),
				'lane',
				'rate',
			];
		} else {
			columns = ['select', 'pickupDate', 'lane', 'rate'];
		}
	} else {
		if (isBookedLoads) {
			columns = [
				...(rowSelectableEnabled ? ['select'] : []),
				'reference_number',
				'po_number',
				'pickupDate',
				'deliveryDate',
				'lane',
				'stopsCount',
				'rate',
				'business_name',
				'userInfo',
				'assignedAt',
			];
		} else {
			columns = [
				'select',
				'reference_number',
				'po_number',
				'pickupDate',
				'lane',
				'stopsCount',
				'rate',
				'offeredRate',
				'deliveryDate',
				'business_name',
				'userInfo',
				'broker_onboard_link',
			];
		}
	}

	return columns;
}

export const LOADS_SOURCING_KEYS = {
	ACCOUNT_ID: 'LOADS_SOURCING_ACCOUNT_ID',
	EQUIPMENT: 'LOADS_SOURCING_EQUIPMENT',
};

export const SORT_FIELDS = [
	LOAD_FIELDS.PO_NUMBER,
	LOAD_FIELDS.COMPANY,
	LOAD_FIELDS.PICKUP_DATE,
	LOAD_FIELDS.ORIGIN_CITY,
	LOAD_FIELDS.ORIGIN_STATE,
	LOAD_FIELDS.DESTINATION_CITY,
	LOAD_FIELDS.DESTINATION_STATE,
	LOAD_FIELDS.DELIVERY_DATE,
	LOAD_FIELDS.USER,
];

export const FIELDS_MAP = {
	[LOAD_FIELDS.REFERENCE_NUMBER]: 'reference_number',
	[LOAD_FIELDS.PICKUP_DATE]: 'pickupDate',
	[LOAD_FIELDS.DELIVERY_DATE]: 'deliveryDate',
	[LOAD_FIELDS.OFFERED_RATE]: 'offeredRate',
	[LOAD_FIELDS.USER]: 'fullName',
	[LOAD_FIELDS.ORIGIN_CITY]: ['origin', 'city'],
	[LOAD_FIELDS.ORIGIN_STATE]: ['origin', 'state'],
	[LOAD_FIELDS.DESTINATION_CITY]: ['destination', 'city'],
	[LOAD_FIELDS.DESTINATION_STATE]: ['destination', 'state'],
	[LOAD_FIELDS.CREATED]: ['quoteDTO', 'created'],
	[LOAD_FIELDS.EQUIPMENT]: 'trailer_type',
	[LOAD_FIELDS.COMPANY]: 'business_name',
	[LOAD_FIELDS.WEIGHT]: 'weight',
};

export const FILTER_FIELDS = [
	LOAD_FIELDS.REFERENCE_NUMBER,
	LOAD_FIELDS.PO_NUMBER,
	LOAD_FIELDS.COMPANY,
	LOAD_FIELDS.PICKUP_DATE,
	LOAD_FIELDS.ORIGIN_CITY,
	LOAD_FIELDS.ORIGIN_STATE,
	LOAD_FIELDS.DESTINATION_CITY,
	LOAD_FIELDS.DESTINATION_STATE,
	LOAD_FIELDS.DELIVERY_DATE,
	LOAD_FIELDS.EQUIPMENT,
	LOAD_FIELDS.USER,
	LOAD_FIELDS.WEIGHT,
];

export let filterFieldsInputTypeMap: Map<string, string> = new Map<string, string>();
filterFieldsInputTypeMap.set(LOAD_FIELDS.REFERENCE_NUMBER, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(LOAD_FIELDS.PO_NUMBER, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(LOAD_FIELDS.PICKUP_DATE, INPUT_TYPES.DATE);
filterFieldsInputTypeMap.set(LOAD_FIELDS.ORIGIN_CITY, INPUT_TYPES.LOCATION);
filterFieldsInputTypeMap.set(LOAD_FIELDS.ORIGIN_STATE, INPUT_TYPES.STATE);
filterFieldsInputTypeMap.set(LOAD_FIELDS.DESTINATION_CITY, INPUT_TYPES.LOCATION);
filterFieldsInputTypeMap.set(LOAD_FIELDS.DESTINATION_STATE, INPUT_TYPES.STATE);
filterFieldsInputTypeMap.set(LOAD_FIELDS.DELIVERY_DATE, INPUT_TYPES.DATE);
filterFieldsInputTypeMap.set(LOAD_FIELDS.EQUIPMENT, INPUT_TYPES.TRAILER_TYPE);
filterFieldsInputTypeMap.set(LOAD_FIELDS.OFFERED_RATE, INPUT_TYPES.NUMBER);
filterFieldsInputTypeMap.set(LOAD_FIELDS.COMPANY, INPUT_TYPES.CARRIER_ACCOUNTS);
filterFieldsInputTypeMap.set(LOAD_FIELDS.USER, INPUT_TYPES.STRING);
filterFieldsInputTypeMap.set(LOAD_FIELDS.WEIGHT, INPUT_TYPES.NUMBER);

export const getNestedValue = (obj: any, path: string[]): any =>
	path.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);

export const NUMBER_FIELDS = new Set([LOAD_FIELDS.RATE, LOAD_FIELDS.OFFERED_RATE, LOAD_FIELDS.WEIGHT]);

export const STATE_FIELDS = new Set([LOAD_FIELDS.ORIGIN_STATE, LOAD_FIELDS.DESTINATION_STATE]);

export const SERVER_FILTER_MAP = {
	[LOAD_FIELDS.REFERENCE_NUMBER]: 'referenceNumber',
	[LOAD_FIELDS.PICKUP_DATE]: 'pickupDate',
	[LOAD_FIELDS.DELIVERY_DATE]: 'deliveryDate',
	[LOAD_FIELDS.ORIGIN_CITY]: 'originCity',
	[LOAD_FIELDS.ORIGIN_STATE]: 'originState',
	[LOAD_FIELDS.DESTINATION_CITY]: 'destinationCity',
	[LOAD_FIELDS.DESTINATION_STATE]: 'destinationState',
	[LOAD_FIELDS.EQUIPMENT]: 'equipmentTypes',
	[LOAD_FIELDS.COMPANY]: 'accountIds',
	[LOAD_FIELDS.USER]: 'userName',
	[LOAD_FIELDS.PO_NUMBER]: 'poNumber',
	[LOAD_FIELDS.WEIGHT]: 'maxWeight',
};

export const SERVER_SORT_MAP = {
	[LOAD_FIELDS.PICKUP_DATE]: 'pickupDate',
	[LOAD_FIELDS.DELIVERY_DATE]: 'deliveryDate',
	[LOAD_FIELDS.ORIGIN_CITY]: 'originCity',
	[LOAD_FIELDS.ORIGIN_STATE]: 'originState',
	[LOAD_FIELDS.DESTINATION_CITY]: 'destinationCity',
	[LOAD_FIELDS.DESTINATION_STATE]: 'destinationState',
	[LOAD_FIELDS.COMPANY]: 'businessName',
	[LOAD_FIELDS.USER]: 'userName',
	[LOAD_FIELDS.PO_NUMBER]: 'poNumber',
};
